<template>
  <div class="col-md-12 mb-6">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <label class="fs-6 fw-bold form-label mt-3 mb-0">
        {{ attribute.label }} <span class="text-danger" v-if="attribute.required">*</span>
      </label>
      <div class="d-flex flex-wrap align-items-center gap-2">
        <button type="button" class="btn btn-sm btn-light" :disabled="disabled" @click="clearCanvas">Limpar</button>
        <button type="button" class="btn btn-sm btn-primary" :disabled="disabled || saving" @click="saveAnnotation">
          {{ saving ? 'Salvando...' : 'Salvar desenho' }}
        </button>
      </div>
    </div>

    <div v-if="loadingBackground" class="text-muted mb-2">Carregando imagem...</div>

    <canvas
        ref="annotationCanvas"
        class="annotation-canvas"
        width="900"
        height="450"
        @mousedown="startDraw"
        @mousemove="draw"
        @mouseup="stopDraw"
        @mouseleave="stopDraw"
        @touchstart.prevent="startDrawTouch"
        @touchmove.prevent="drawTouch"
        @touchend.prevent="stopDraw"
    />
  </div>
</template>

<script>
import http from "../../../helpers/http";
import fundoDeOlhoSvg from "../../../assets/ophthalmology/fundo_de_olho.svg";
import corneaSvg from "../../../assets/ophthalmology/cornea.svg";
import palpebraSvg from "../../../assets/ophthalmology/palpebra.svg";
import Alert from "../../../helpers/alert";

export default {
  name: "ImageAnnotationInput",
  emits: ['onChange'],
  props: {
    attribute: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    schedule: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isDrawing: false,
      context: null,
      backgroundImage: null,
      savedAnnotationLoaded: false,
      savedFileId: null,
      saving: false,
      loadingBackground: false,
      lastPoint: {x: 0, y: 0},
      objectUrls: [],
      knownAssets: {
        fundo_de_olho: fundoDeOlhoSvg,
        cornea: corneaSvg,
        palpebra: palpebraSvg,
      },
    };
  },
  mounted() {
    this.context = this.$refs.annotationCanvas.getContext('2d');
    this.context.lineWidth = 2;
    this.context.strokeStyle = '#d90000';
    this.context.lineJoin = 'round';
    this.context.lineCap = 'round';
    this.initializeCanvas();
  },
  beforeUnmount() {
    this.revokeObjectUrls();
  },
  methods: {
    async initializeCanvas() {
      const value = this.parseCurrentValue();
      this.savedFileId = value.id_file ?? null;

      if (this.savedFileId) {
        await this.loadImageFromFileId(this.savedFileId, true);
        return;
      }

      await this.loadBackground();
    },
    async loadBackground() {
      this.savedAnnotationLoaded = false;
      this.savedFileId = null;
      const source = this.attribute?.options?.background_source ?? 'catalog';

      if (source === 'catalog') {
        const asset = this.attribute?.options?.image_asset;
        const imageSource = this.knownAssets[asset] ?? null;
        if (!imageSource) {
          this.clearCanvasSurface();
          return;
        }

        await this.loadImageFromSource(imageSource, false);
        return;
      }

      if (source === 'file' && this.attribute?.options?.id_file) {
        await this.loadImageFromFileId(this.attribute.options.id_file, false);
      }
    },
    loadImageFromSource(source, asCurrentCanvas) {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
          if (asCurrentCanvas) {
            this.drawImageOnCanvas(image);
            this.savedAnnotationLoaded = true;
            this.backgroundImage = null;
          } else {
            this.backgroundImage = image;
            this.redrawCanvas();
          }
          resolve();
        };
        image.onerror = () => resolve();
        image.src = source;
      });
    },
    async loadImageFromFileId(fileId, asCurrentCanvas) {
      this.loadingBackground = true;
      try {
        const response = await http.get(`/record-types/files/${fileId}`, {responseType: 'blob'});
        const objectUrl = URL.createObjectURL(response.data);
        this.objectUrls.push(objectUrl);
        await this.loadImageFromSource(objectUrl, asCurrentCanvas);
      } catch (_) {
        this.clearCanvasSurface();
      } finally {
        this.loadingBackground = false;
      }
    },
    drawImageOnCanvas(image) {
      const canvas = this.$refs.annotationCanvas;
      this.context.clearRect(0, 0, canvas.width, canvas.height);
      this.context.drawImage(image, 0, 0, canvas.width, canvas.height);
    },
    redrawCanvas() {
      const canvas = this.$refs.annotationCanvas;
      this.context.clearRect(0, 0, canvas.width, canvas.height);
      if (this.backgroundImage) {
        this.context.drawImage(this.backgroundImage, 0, 0, canvas.width, canvas.height);
      }
    },
    clearCanvasSurface() {
      const canvas = this.$refs.annotationCanvas;
      this.context.clearRect(0, 0, canvas.width, canvas.height);
    },
    getMousePosition(event) {
      const rect = this.$refs.annotationCanvas.getBoundingClientRect();
      const scaleX = this.$refs.annotationCanvas.width / rect.width;
      const scaleY = this.$refs.annotationCanvas.height / rect.height;

      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
      };
    },
    startDraw(event) {
      if (this.disabled) {
        return;
      }

      this.isDrawing = true;
      this.lastPoint = this.getMousePosition(event);
    },
    draw(event) {
      if (!this.isDrawing || this.disabled) {
        return;
      }

      const current = this.getMousePosition(event);
      this.context.beginPath();
      this.context.moveTo(this.lastPoint.x, this.lastPoint.y);
      this.context.lineTo(current.x, current.y);
      this.context.stroke();
      this.lastPoint = current;
    },
    stopDraw() {
      this.isDrawing = false;
    },
    startDrawTouch(event) {
      if (!event.touches?.length) {
        return;
      }

      this.startDraw(event.touches[0]);
    },
    drawTouch(event) {
      if (!event.touches?.length) {
        return;
      }

      this.draw(event.touches[0]);
    },
    async clearCanvas() {
      if (this.savedAnnotationLoaded) {
        await this.loadBackground();
      } else {
        this.redrawCanvas();
      }
      this.emitChange();
    },
    emitChange(payload = null) {
      const current = payload ?? this.buildCurrentValue();
      this.$emit('onChange', {
        ...this.attribute,
        text: this.syncAttributeText(current),
      });
    },
    buildCurrentValue() {
      const value = {
        background_source: this.attribute?.options?.background_source ?? 'catalog',
        image_asset: this.attribute?.options?.image_asset ?? null,
      };

      if (this.savedFileId) {
        value.id_file = this.savedFileId;
      }

      return value;
    },
    syncAttributeText(value) {
      const serialized = JSON.stringify(value);
      if (this.attribute) {
        this.attribute.text = serialized;
      }
      return serialized;
    },
    parseCurrentValue() {
      const text = this.attribute?.text;
      if (!text) {
        return {};
      }

      if (typeof text === 'string') {
        try {
          return JSON.parse(text);
        } catch (_) {
          return {};
        }
      }

      return text;
    },
    saveAnnotation() {
      if (this.disabled) {
        return;
      }

      const schedule = this.schedule;
      if (!schedule?.id_prof || !schedule?.id_patient || !this.attribute?.id_medrec || !this.attribute?.id_medrec_attr) {
        this.emitChange();
        return;
      }

      this.saving = true;
      this.$refs.annotationCanvas.toBlob((blob) => {
        if (!blob) {
          this.saving = false;
          return;
        }

        const file = new File([blob], `annotation-${this.attribute.id_medrec_attr}.png`, {type: 'image/png'});
        const formData = new FormData();
        formData.append('file', file);

        const url = `/professionals/${schedule.id_prof}/patients/${schedule.id_patient}/medical-records/${this.attribute.id_medrec}/attributes/${this.attribute.id_medrec_attr}/image-annotation/upload`;
        http.post(url, formData).then((response) => {
          this.savedAnnotationLoaded = true;
          this.savedFileId = response.data.value?.id_file ?? null;
          this.emitChange(response.data.value);
          Alert.toastSuccess('Desenho salvo com sucesso.');
        }).catch(() => {
          Alert.toastError('Falha ao salvar desenho.');
        }).finally(() => {
          this.saving = false;
        });
      }, 'image/png');
    },
    revokeObjectUrls() {
      this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.objectUrls = [];
    },
  },
};
</script>

<style scoped>
.annotation-canvas {
  width: 100%;
  max-width: 900px;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  background: #fff;
  touch-action: none;
}
</style>
